import { QuizConfig } from './types'
import { GB_EXIT_PATH } from '../components/constants'

const e10: QuizConfig = {
  productType: 'membership',
  screens: [
    // ============================================================
    // SECTION 1: Intro
    // ============================================================
    {
      path: '/e10/welcome-age-range',
      type: 'SelectScreen',
      hideProgressBar: true,
      props: {
        title: 'Transform your health with the <green>power of your gut</green>',
        description: 'Choose your age to start',
        questionKey: 'welcome_age_range',
        hideBackLink: true,
        directNavigation: true,
        columns: 2,
        showArrow: true,
        topImage: '/img/e4/table.jpg',
        topImageAspectRatio: '335 / 250',
        topImageAlt: 'People sharing a meal around a table',
        options: [
          { label: 'Age: 18-34', value: '18_34' },
          { label: 'Age: 35-49', value: '35_49' },
          { label: 'Age: 50-64', value: '50_64' },
          { label: 'Age: 65+', value: '65_plus' },
        ],
      },
    },
    {
      path: '/e10/social-proof',
      type: 'SocialProofScreen',
      hideProgressBar: true,
      props: {
        title: '3 million people',
        description: 'rely on ZOE each month to transform their health',
        topImage: '/img/e10/social-proof.png',
        topImageAspectRatio: '1149 / 750',
        topImageAlt: 'ZOE members',
        topImageBleed: true,
        // Same collage and spacing as e9: 55px in Figma between collage and
        // headline (48 is the nearest DS step), 8px headline→subtitle.
        tightTop: true,
        topImageSpacing: 32,
        descriptionSpacing: 8,
        titleVariant: 'large',
        laurelGap: 32,
        laurelSize: 18,
        descriptionPaddingX: 40,
        // TODO: use a design system colour token instead of a raw hex.
        leafColor: '#E5D9C8',
      },
    },
    {
      path: '/e10/goals',
      type: 'MultiSelectScreen',
      props: {
        title: 'What do you want to achieve?',
        description: 'Choose as many as you want',
        questionKey: 'goals',
        options: [
          { label: 'Live longer', value: 'live_longer' },
          { label: 'Lose weight', value: 'lose_weight' },
          { label: 'Improve my gut health', value: 'improve_gut_health' },
          { label: 'Feel better', value: 'feel_better' },
          { label: 'Sleep better', value: 'sleep_better' },
        ],
      },
    },
    {
      path: '/e10/first-step',
      type: 'ContentScreen',
      props: {
        tightTop: true,
        largeTitle: true,
        title: 'You\u2019ve taken the first step!',
        description:
          "What you want to achieve leads back to one thing: your gut.{br}{br}Let's build a plan {goalEnding}",
        goalTokens: {
          goalEnding: {
            improve_gut_health: 'to improve your gut health.',
            lose_weight: 'to help you lose weight, sustainably.',
            feel_better: 'to help you feel better, day to day.',
            sleep_better: 'to help you sleep better.',
            live_longer: 'to support a longer, healthier life.',
            multiple: 'to get you in sync with it.',
          },
        },
        topImage: '/img/e10/first-step.jpg',
        topImageAlt: 'Person enjoying healthy food in a kitchen',
        topImageAspectRatio: '1149 / 750',
      },
    },
    {
      path: '/e10/heard-about-gut-health',
      type: 'SelectScreen',
      props: {
        title: 'Have you heard about gut health?',
        questionKey: 'heard_about_gut_health',
        options: [
          {
            label: 'Just the name',
            value: 'just_the_name',
            playback:
              '<b>You\u2019re not alone!</b> Even scientists are only recently realising the role gut plays in overall health. We can help you find out.',
          },
          {
            label: 'I know a few things',
            value: 'a_few_things',
            playback:
              "<b>Nice start!</b> We'll build on your knowledge, starting with why your gut matters more than you might think.",
          },
          {
            label: 'I know everything about it!',
            value: 'everything',
            playback:
              "<b>Great stuff!</b> We'll dive deeper into the science from the world\u2019s largest gut health study.",
          },
        ],
      },
    },
    {
      path: '/e10/zoe-science',
      type: 'MandatoryConsentScreen',
      hideProgressBar: true,
      props: {
        title: 'We\u2019re the world leaders in gut health',
        description: 'We personalise your plan with the world\u2019s largest microbiome DNA database.',
        topImage: '/img/e4/scientists.jpg',
        topImageAlt:
          'ZOE scientists in partnership with Kings College London, Stanford Medicine, and Massachusetts General Hospital',
        topImageAspectRatio: '335 / 250',
        consentLabel:
          'I agree to ZOE using my diet and health data to provide and personalise my quiz and onboarding experience.',
        consentQuestionKey: 'data_consent',
      },
    },

    // ============================================================
    // SECTION 2: Energy and sleep
    // ============================================================
    {
      path: '/e10/energy',
      type: 'SelectScreen',
      props: {
        title: 'How is your energy during the day?',
        questionKey: 'energy',
        options: [
          { label: 'Low, I feel tired all day', value: 'low' },
          { label: 'Inconsistent, I get slumps', value: 'inconsistent' },
          {
            label: 'High, I feel steady all day',
            value: 'high',
            playback: '<b>Amazing!</b> We\u2019ll help you fine-tune what\u2019s already working.',
          },
        ],
      },
    },
    {
      path: '/e10/energy-playback',
      type: 'ContentScreen',
      showWhen: { energy: ['low', 'inconsistent'] },
      props: {
        title: 'People who follow ZOE are 4x more likely to report better energy',
        description:
          "Food shapes how steady your energy feels throughout the day. We'll show you how to eat to fuel your gut and keep your energy stable.",
        regularFooter: true,
        topImage: '/img/e10/energy-playback.png',
        squareImage: true,
        topImageAspectRatio: '800 / 605',
        imageObjectFit: 'contain',
        tightTop: true,
        tightFooter: true,
        footerBody: 'Source: ZOE research in Nature Medicine, 2024',
      },
    },
    {
      path: '/e10/sleep-hours',
      type: 'SelectScreen',
      props: {
        title: 'How much sleep do you usually get?',
        questionKey: 'sleep_hours',
        options: [
          { label: 'Less than 5 hours', value: 'less_than_5' },
          { label: '5-6 hours', value: '5_6' },
          { label: '7-8 hours', value: '7_8' },
          { label: 'More than 8 hours', value: '8_plus' },
        ],
      },
    },
    {
      path: '/e10/sleep-quality',
      type: 'SelectScreen',
      props: {
        title: 'How is the quality of your sleep',
        questionKey: 'sleep_quality',
        options: [
          {
            label: 'Good, I sleep well',
            value: 'sleep_well',
            playback: '<b>Great stuff!</b> Quality sleep supports a healthy, diverse gut microbiome.',
          },
          { label: 'I struggle falling asleep', value: 'struggle_falling_asleep' },
          { label: 'I wake up tired', value: 'wake_up_tired' },
          { label: 'I wake during the night', value: 'wake_during_night' },
          { label: 'I get hot flushes', value: 'hot_flushes' },
        ],
      },
    },
    {
      path: '/e10/sleep-playback',
      type: 'ContentScreen',
      showWhen: { sleep_quality: ['struggle_falling_asleep', 'wake_up_tired', 'wake_during_night', 'hot_flushes'] },
      props: {
        title: 'People who follow ZOE are 4x more likely to report better sleep',
        description: 'Poor sleep is frustrating and affects your health, but ZOE is scientifically proven to work.',
        regularFooter: true,
        topImage: '/img/e10/sleep-playback.png',
        squareImage: true,
        topImageAspectRatio: '800 / 602',
        imageObjectFit: 'contain',
        tightTop: true,
        tightFooter: true,
        footerBody: 'Source: ZOE research in Nature Medicine, 2024',
      },
    },
    {
      path: '/e10/diet-type',
      type: 'MultiSelectScreen',
      props: {
        title: 'Do you follow a specific diet?',
        description: 'Select all that apply',
        questionKey: 'diet_type',
        options: [
          { label: 'No, I eat everything', value: 'no', exclusive: true },
          { label: 'Vegetarian', value: 'vegetarian', exclusiveGroup: 'diet_pattern' },
          { label: 'Vegan', value: 'vegan', exclusiveGroup: 'diet_pattern' },
          { label: 'Pescatarian', value: 'pescatarian', exclusiveGroup: 'diet_pattern' },
          { label: 'Gluten-free', value: 'gluten_free' },
          { label: 'Dairy-free', value: 'dairy_free' },
          { label: 'Other', value: 'other' },
        ],
      },
    },

    // ============================================================
    // SECTION 3: Diet quality
    // ============================================================
    {
      path: '/e10/diet-quality',
      type: 'SelectScreen',
      props: {
        title: 'How would you rate your diet quality?',
        questionKey: 'diet_quality',
        options: [
          {
            label: 'It\u2019s not as healthy as I\u2019d like',
            value: 'poor',
            playback:
              '<b>29% of people start right here.</b> We\u2019ll help you make simple, personalised changes that fit your lifestyle.',
          },
          {
            label: 'It varies day-to-day',
            value: 'average',
            playback:
              '<b>44% of people say their diet varies too.</b> We\u2019ll show you what to tweak, and help you turn those changes into habits.',
          },
          {
            label: 'It\u2019s healthy and well-balanced',
            value: 'good',
            playback:
              '<b>Great foundations!</b> We\u2019ll give you personalised recommendations to build on what\u2019s already working for you.',
          },
        ],
      },
    },
    {
      path: '/e10/snacker',
      type: 'SelectScreen',
      props: {
        title: 'Are you a snacker?',
        questionKey: 'snacker',
        options: [
          {
            label: 'Yes',
            value: 'yes',
            playback:
              '<b>Snacking isn\u2019t the enemy.</b> Our study showed that snack quality matters more than frequency. We\u2019ll make healthy snacking easy.',
          },
          {
            label: 'No',
            value: 'no',
            playback:
              "<b>Not a snacker?</b> Our study showed snack quality matters more than frequency. We'll focus on meals that keep you full throughout the day.",
          },
        ],
      },
    },
    {
      path: '/e10/processed-food-frequency',
      type: 'SelectScreen',
      props: {
        title: 'How often do you eat processed foods?',
        description: 'Examples include shop-bought bread, sweeteners, fizzy drinks, ready meals, and breakfast cereals',
        questionKey: 'processed_food_frequency',
        options: [
          {
            label: 'Most meals',
            value: 'most_meals',
            playback:
              "<b>That's more common than you'd think!</b> 60% of what we eat today is processed, engineered to be hard to resist. We'll focus on simple swaps, not bans.",
          },
          {
            label: 'Once a day',
            value: 'once_a_day',
            playback:
              "<b>Good to know!</b> 60% of what we eat today is processed, engineered to be hard to resist. We'll focus on simple swaps, not bans.",
          },
          {
            label: 'A few times a week',
            value: 'few_times_a_week',
            playback:
              "<b>Good to know!</b> Processed food makes up 60% of what most of us eat, so you're already ahead. We'll help you build on it.",
          },
          {
            label: 'Rarely or never',
            value: 'rarely_never',
            playback:
              "<b>Great start!</b> Processed food makes up 60% of what most of us eat, so you're already ahead. We'll help you build on it.",
          },
        ],
      },
    },
    {
      path: '/e10/plant-frequency',
      type: 'SelectScreen',
      props: {
        title: 'How many plants do you eat a day?',
        description: 'Examples include fruits, vegetables, whole grains, nuts, seeds, herbs, and spices',
        questionKey: 'plant_frequency',
        options: [
          {
            label: '1-3 plants',
            value: '1_3',
            playback:
              '<b>Good start!</b> Our research shows eating 30 <i>different</i> plants a week is one of the best things you can do for your gut. We can help you get there.',
          },
          {
            label: '4-6 plants',
            value: '4_6',
            playback:
              '<b>Great start!</b> Our research shows eating 30 <i>different</i> plants a week is one of the most powerful things you can do for your gut.',
          },
          {
            label: '7+ plants',
            value: '7_plus',
            playback:
              '<b>Amazing!</b> Our research shows eating 30 <i>different</i> plants a week is one of the best things you can do for your gut, so variety is important too.',
          },
        ],
      },
    },
    {
      path: '/e10/thanks-for-sharing',
      type: 'ContentScreen',
      props: {
        title: 'Thanks for sharing!',
        description: 'Your gut is unique. With our science, your diet will be too.',
        topImage: '/img/e4/thanks-for-sharing.png',
        topImageAspectRatio: '335 / 286',
        tightTop: true,
      },
    },

    // ============================================================
    // SECTION 4a: About you (no weight loss)
    // ============================================================
    {
      path: '/e10/lifestyle-activity',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'How active are you?',
        questionKey: 'lifestyle_activity',
        options: [
          {
            label: 'Not active \u2013 I have limited mobility or cannot exercise',
            value: 'not_active',
            playback:
              '<b>That\u2019s okay!</b> There are plenty of ways to support your health without exercise. Your gut is a great place to start.',
          },
          {
            label: 'Mostly sedentary \u2013 I sit for most of the day',
            value: 'mostly_sedentary',
            playback:
              '<b>28% of responders say the same.</b> Even small amounts of movement can help your body and gut microbiome.',
          },
          {
            label: 'Lightly active \u2013 I move often',
            value: 'lightly_active',
            playback: '<b>Nice!</b> Exercise isn\u2019t just good for your body, it benefits your gut microbiome too.',
          },
          {
            label: 'Very active \u2013 I have a physical job or exercise regularly',
            value: 'very_active',
            playback: '<b>Great!</b> We\u2019ll give you science-backed nutrition advice to fuel your activities.',
          },
        ],
      },
    },
    {
      path: '/e10/biological-sex',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'What sex were you assigned at birth?',
        description: 'Biological sex affects gut microbiome composition',
        questionKey: 'biological_sex',
        options: [
          { label: 'Male', value: 'male' },
          { label: 'Female', value: 'female' },
        ],
      },
    },
    {
      path: '/e10/height',
      type: 'InputHeightScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'What\u2019s your height?',
        subtitle: 'Your height influences your metabolism and helps shape our recommendations.',
        defaultUnit: 'ft',
      },
    },
    {
      path: '/e10/current-weight',
      type: 'InputWeightScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      useAltProps: { quiz_country: 'US' },
      props: {
        title: 'What\u2019s your current weight?',
        description: 'We use this to tailor your plan by understanding your body\u2019s unique metabolism.',
        defaultWeightUnit: 'kg',
      },
      altProps: {
        title: 'What\u2019s your current weight?',
        description: 'We use this to tailor your plan by understanding your body\u2019s unique metabolism.',
        defaultWeightUnit: 'kg',
      },
    },
    {
      path: '/e10/antibiotics',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'Have you taken antibiotics in the last three years?',
        questionKey: 'antibiotics',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback:
              '<b>Antibiotics save lives, but they can also harm your gut.</b> The good news? With our plan, you can start to improve your gut health in just a few days.',
          },
          {
            label: 'No',
            value: 'false',
            playback:
              '<b>Great!</b> Antibiotics can harm your gut and kill good gut bacteria, so you\u2019re off to a great start.',
          },
        ],
      },
    },
    {
      path: '/e10/blood-tests',
      type: 'SelectScreen',
      showWhen: { no_weight_loss_goal: 'true' },
      props: {
        title: 'Have you had any blood tests in the last two years?',
        questionKey: 'blood_tests',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback:
              '<b>Great!</b> Once you start your plan, share your results to unlock more tailored insights into your blood sugar and fat responses to food.',
          },
          { label: 'No', value: 'false' },
        ],
      },
    },

    // ============================================================
    // SECTION 4b: About you (weight loss)
    // ============================================================
    {
      path: '/e10/wl-biological-sex',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'What sex were you assigned at birth?',
        description: 'Biological sex affects gut microbiome composition',
        questionKey: 'biological_sex',
        options: [
          { label: 'Male', value: 'male' },
          { label: 'Female', value: 'female' },
        ],
      },
    },
    {
      path: '/e10/wl-height',
      type: 'InputHeightScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'What\u2019s your height?',
        subtitle: 'Your height influences your metabolism and helps shape our recommendations.',
        defaultUnit: 'ft',
      },
    },
    {
      path: '/e10/wl-current-weight',
      type: 'InputWeightScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      useAltProps: { quiz_country: 'US' },
      props: {
        title: 'What\u2019s your current weight?',
        description: 'We use this to tailor your plan by understanding your body\u2019s unique metabolism.',
        defaultWeightUnit: 'kg',
      },
      altProps: {
        title: 'What\u2019s your current weight?',
        description: 'We use this to tailor your plan by understanding your body\u2019s unique metabolism.',
        defaultWeightUnit: 'kg',
      },
    },
    {
      path: '/e10/future-weight',
      type: 'InputTargetWeightScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'And what weight would you like to be?',
        descriptionLbs: 'Weight loss can come naturally as you transform your gut health.',
        descriptionKg: 'Weight loss can come naturally as you transform your gut health.',
      },
    },
    {
      path: '/e10/weight-proven',
      type: 'ContentScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Better gut health leads to lasting weight loss',
        description:
          'In our clinical trial, those who followed ZOE advice reduced their waistline by over 2 inches and lost over 4 kg, all without counting calories or restricting foods.',
        topImage: '/img/e4/weight-proven.png',
        topImageAspectRatio: '335 / 250',
        titleAboveImage: true,
        imageObjectFit: 'contain',
        tightTop: true,
        tightImage: true,
        tightFooter: true,
        regularFooter: true,
        footerBody: 'Source: ZOE research in Nature Medicine, 2024',
      },
    },
    {
      path: '/e10/wl-lifestyle-activity',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'How active are you?',
        questionKey: 'lifestyle_activity',
        options: [
          {
            label: 'Not active \u2013 I have limited mobility or cannot exercise',
            value: 'not_active',
            playback:
              '<b>That\u2019s okay!</b> There are plenty of ways to support your health without exercise. Your gut is a great place to start.',
          },
          {
            label: 'Mostly sedentary \u2013 I sit for most of the day',
            value: 'mostly_sedentary',
            playback:
              '<b>28% of responders say the same.</b> Even small amounts of movement can help your body and gut microbiome.',
          },
          {
            label: 'Lightly active \u2013 I move often',
            value: 'lightly_active',
            playback: '<b>Nice!</b> Exercise isn\u2019t just good for your body, it benefits your gut microbiome too.',
          },
          {
            label: 'Very active \u2013 I have a physical job or exercise regularly',
            value: 'very_active',
            playback: '<b>Great!</b> We\u2019ll give you science-backed nutrition advice to fuel your activities.',
          },
        ],
      },
    },
    {
      path: '/e10/wl-glp1s',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Are you currently taking GLP-1 weight loss medication?',
        description: 'Examples include Wegovy, Mounjaro, and Saxenda',
        questionKey: 'glp1s',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback:
              '<b>ZOE is the perfect companion for your weight loss journey.</b> We\u2019ll help you make lasting health improvements, choose smarter foods, and give your body the nutrients it needs to thrive.',
          },
          { label: 'No', value: 'false' },
        ],
      },
    },
    {
      path: '/e10/wl-antibiotics',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Have you taken antibiotics in the last three years?',
        questionKey: 'antibiotics',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback:
              '<b>Antibiotics save lives, but they can also harm your gut.</b> The good news? With our plan, you can start to improve your gut health in just a few days.',
          },
          {
            label: 'No',
            value: 'false',
            playback:
              '<b>Great!</b> Antibiotics can harm your gut and kill good gut bacteria, so you\u2019re off to a great start.',
          },
        ],
      },
    },
    {
      path: '/e10/wl-blood-tests',
      type: 'SelectScreen',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        title: 'Have you had any blood tests in the last two years?',
        questionKey: 'blood_tests',
        options: [
          {
            label: 'Yes',
            value: 'true',
            playback:
              '<b>Great!</b> Once you start your plan, share your results to unlock more tailored insights into your blood sugar and fat responses to food.',
          },
          { label: 'No', value: 'false' },
        ],
      },
    },
    {
      path: '/e10/goal-graph',
      type: 'WeightCalculationScreen',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      showWhen: { has_weight_loss_goal: 'true' },
      props: {
        projectionGraph: true,
        title: 'Transform your gut health and lose weight with ZOE',
        description:
          'Based on your answers, we predict you\u2019ll lose weight with ZOE, <b>sleep better, have more energy, live longer,</b> and much more.',
      },
    },

    // ============================================================
    // SECTION 4a/4b CONVERGENCE: Health Summary
    // ============================================================
    {
      path: '/e10/health-summary',
      type: 'HealthSummaryScreen',
      hideProgressBar: true,
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      props: {
        title: 'Here\u2019s your health summary',
        regularLabels: true,
        summaryItems: [
          {
            label: 'Sleep duration',
            questionKey: 'sleep_hours',
            icon: 'moon',
            formatAnswer: {
              less_than_5: 'Less than 5 hours',
              '5_6': '5-6 hours',
              '7_8': '7-8 hours',
              '8_plus': 'More than 8 hours',
            },
          },
          {
            label: 'Sleep quality',
            questionKey: 'sleep_quality',
            icon: 'cgm',
            formatAnswer: {
              sleep_well: 'Good',
              struggle_falling_asleep: 'Average',
              wake_up_tired: 'Average',
              wake_during_night: 'Poor',
              hot_flushes: 'Poor',
            },
          },
          {
            label: 'Energy',
            questionKey: 'energy',
            icon: 'flash',
            formatAnswer: {
              low: 'Low',
              inconsistent: 'Inconsistent',
              high: 'High',
            },
          },
          {
            label: 'Lifestyle',
            questionKey: 'lifestyle_activity',
            icon: 'body-fat',
            formatAnswer: {
              not_active: 'Not active',
              mostly_sedentary: 'Sedentary',
              lightly_active: 'Lightly active',
              very_active: 'Very active',
            },
          },
          {
            label: 'Diet type',
            questionKey: 'diet_type',
            icon: 'food-drink',
            formatAnswer: {
              no: 'Omnivore',
              vegetarian: 'Vegetarian',
              vegan: 'Vegan',
              pescatarian: 'Pescatarian',
              gluten_free: 'Gluten-free',
              dairy_free: 'Dairy-free',
              other: 'Other',
            },
          },
          {
            label: 'Diet quality',
            questionKey: 'diet_quality',
            icon: 'generic-food',
            // Square glyph: 16px inside the 24px column matches the other icons.
            iconSize: 16,
            formatAnswer: {
              poor: 'Low',
              average: 'Average',
              good: 'High',
            },
          },
          {
            label: 'Current weight',
            questionKey: 'current_weight',
            icon: 'scales',
            formatFn: 'weightWithUnit' as const,
            showWhen: { has_weight_loss_goal: 'true' },
          },
          {
            label: 'Ideal weight',
            questionKey: 'ideal_weight',
            icon: 'calendar-2',
            formatFn: 'weightWithUnit' as const,
            showWhen: { has_weight_loss_goal: 'true' },
          },
        ],
      },
    },

    // ============================================================
    // SECTION 5: Gut health quiz (convergence — all users)
    // ============================================================
    {
      path: '/e10/motivation',
      type: 'SelectScreen',
      props: {
        title: 'What\u2019s your main motivation?',
        questionKey: 'motivation',
        options: [
          {
            label: 'Getting healthier',
            value: 'getting_healthier',
            playback:
              '<b>Focusing on health is powerful!</b> Our clinical trial proves that following our advice leads to significant health improvements.',
          },
          {
            label: 'Feeling happier',
            value: 'feeling_happier',
            playback:
              "<b>Your gut and brain are deeply linked.</b> We'll help you feed your gut and boost the chemicals that support your mood.",
          },
          {
            label: 'A recent diagnosis',
            value: 'recent_diagnosis',
            playback: "<b>We're here to help.</b> With ZOE, you'll feel empowered to take control of your health.",
          },
          {
            label: 'Changing how I look',
            value: 'looking_different',
            playback: "<b>Self-love is a strong motivator!</b> We'll support you in reaching your goal.",
          },
          {
            label: 'Something else',
            value: 'other',
            playback: "<b>Every \u201Cwhy\u201D is valid!</b> Whatever yours is, we're here to help you achieve it.",
          },
        ],
      },
    },
    {
      path: '/e10/gut-quiz-intro',
      type: 'ContentScreen',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      props: {
        title: 'We know your gut is the key to transforming your health',
        description:
          'Now, we want to understand where you are on your gut health journey, so we can better personalise your plan.',
        topImage: '/img/e4/gut-illustration.jpg',
        topImageAspectRatio: '335 / 250',
        tightTop: true,
      },
    },
    {
      path: '/e10/gut-dna-question',
      type: 'SelectScreen',
      props: {
        title: 'Your gut is more unique than your DNA',
        description: 'True or false?',
        subtitleAboveTitle: true,
        questionKey: 'gut_dna_question',
        options: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
      },
    },
    {
      path: '/e10/gut-dna-answer',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      type: 'QuizAnswerScreen',
      props: {
        questionKey: 'gut_dna_question',
        correctAnswer: 'true',
        correctIcon: '/img/shared/thumbs-up.svg',
        incorrectIcon: '/img/shared/not-quite.svg',
        description:
          'The gut houses <b>trillions of bacteria, all completely unique to you.</b> Even identical twins share just 34% of the same gut bacteria!',
        footer: 'Source: ZOE research in Nature Medicine, 2020',
      },
    },
    {
      path: '/e10/gut-impacts-health-question',
      type: 'SelectScreen',
      props: {
        title: 'Your gut impacts almost every aspect of your health',
        description: 'True or false?',
        subtitleAboveTitle: true,
        questionKey: 'gut_impacts_health_question',
        options: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
      },
    },
    {
      path: '/e10/gut-impacts-health-answer',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      type: 'QuizAnswerScreen',
      props: {
        questionKey: 'gut_impacts_health_question',
        correctAnswer: 'true',
        correctIcon: '/img/shared/thumbs-up.svg',
        incorrectIcon: '/img/shared/not-quite.svg',
        description:
          'Our understanding of gut microbes has transformed in the last decade. We now know that <b>your gut is the foundation of your health.</b>{br}{br}' +
          'Beyond digestion, it regulates metabolism, influences mood, and houses 70% of your immune system.',
        footer: 'Source: Hou et al., 2022 and Wiertsema et al., 2021',
      },
    },
    {
      path: '/e10/gut-change-in-days-question',
      type: 'SelectScreen',
      props: {
        title: 'It takes at least 3 months to change your gut microbiome',
        description: 'True or false?',
        subtitleAboveTitle: true,
        questionKey: 'gut_change_in_days_question',
        options: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
      },
    },
    {
      path: '/e10/gut-change-in-days-answer',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      type: 'QuizAnswerScreen',
      props: {
        questionKey: 'gut_change_in_days_question',
        correctAnswer: 'false',
        correctIcon: '/img/shared/thumbs-up.svg',
        incorrectIcon: '/img/shared/not-quite.svg',
        description:
          '<b>Your gut microbiome starts to change in just days</b>, and these changes will steadily build over the following months with ZOE.{br}{br}' +
          'By choosing foods that suit your unique biology, you\u2019ll harness the power of your gut to reach your health goals.',
        footer: 'Source: Wu et al., 2012',
      },
    },

    // ============================================================
    // SECTION 5b: Symptom questionnaire ("Symptom score v2")
    // ============================================================
    {
      path: '/e10/symptom-intro',
      type: 'ContentScreen',
      backgroundColor: 'var(--colour-surface-neutral)',
      props: {
        title: 'Get your Symptom Severity Score',
        description:
          'ZOE’s scientists have developed this score based on data from over 100K research participants.{br}{br}' +
          'Now, we’d like to ask you the same questions to assess your symptoms, identify where to focus, and show you how much you could improve.',
        topImage: '/img/e7/symptom-intro.jpg',
        topImageAspectRatio: '990 / 672',
        imageObjectFit: 'contain',
        tightTop: true,
      },
    },
    {
      path: '/e10/symptom-gut-and-digestion',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have these symptoms bothered you in the past week?',
        sliderOptionValues: ['1', '2', '3', '4'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Gut and digestion', colorScheme: 'gut_digestion' },
        questions: [
          { question: 'Digestive discomfort', questionKey: 'digestive_discomfort' },
          { question: 'Bloating', questionKey: 'bloating' },
          { question: 'Acid reflux and indigestion', questionKey: 'acid_reflux_and_indigestion' },
          { question: 'Irregular bowel movements', questionKey: 'irregular_bowel_movements' },
        ],
      },
    },
    {
      path: '/e10/symptom-inflammation',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have these symptoms bothered you in the past week?',
        sliderOptionValues: ['1', '2', '3', '4'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Inflammation', colorScheme: 'inflammation' },
        questions: [
          { question: 'Joint and muscular discomfort', questionKey: 'joint_and_muscular_discomfort' },
          {
            question: 'Decreased physical strength or stamina',
            questionKey: 'decreased_physical_strength_or_stamina',
          },
          { question: 'Skin issues', questionKey: 'skin_issues' },
          { question: 'Hair loss', questionKey: 'hair_loss' },
        ],
      },
    },
    {
      path: '/e10/symptom-energy-and-mood',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have these symptoms bothered you in the past week?',
        sliderOptionValues: ['1', '2', '3', '4'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Energy and mood', colorScheme: 'energy_mood' },
        questions: [
          { question: 'Tiredness and fatigue', questionKey: 'tiredness_and_fatigue' },
          { question: 'Brain fog or trouble concentrating', questionKey: 'brain_fog_or_trouble_concentrating' },
          { question: 'Poor sleep quality', questionKey: 'poor_sleep_quality' },
          { question: 'Stress or low mood', questionKey: 'stress_or_low_mood' },
        ],
      },
    },
    {
      path: '/e10/symptom-appetite-and-weight',
      type: 'MultipleSlidersScreen',
      props: {
        title: 'How much have these symptoms bothered you in the past week?',
        sliderOptionValues: ['1', '2', '3', '4'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Appetite and weight', colorScheme: 'appetite_weight' },
        questions: [
          { question: 'Hunger', questionKey: 'hunger' },
          { question: 'Cravings', questionKey: 'cravings' },
          {
            question: 'Weight gain or difficulty managing weight',
            questionKey: 'weight_gain_or_difficulty_managing_weight',
          },
        ],
      },
    },
    {
      // Conditional: women aged 35+ only (per Figma branch 6b).
      path: '/e10/symptom-hormonal',
      type: 'MultipleSlidersScreen',
      showWhen: { welcome_age_range: ['35_49', '50_64', '65_plus'], biological_sex: 'female' },
      props: {
        title: 'How much have these symptoms bothered you in the past week?',
        sliderOptionValues: ['1', '2', '3', '4'],
        labels: ['Not at all', 'A little', 'Quite a bit', 'Extremely'],
        requiredFieldMissingMessage: 'Please select an option for this symptom.',
        tag: { label: 'Hormonal', colorScheme: 'hormonal' },
        questions: [
          { question: 'Night sweats', questionKey: 'night_sweats' },
          { question: 'Hot flushes', questionKey: 'hot_flushes' },
          { question: 'Irritability', questionKey: 'irritability' },
          { question: 'Loss of interest in sex', questionKey: 'loss_of_interest_in_sex' },
        ],
      },
    },
    {
      path: '/e10/symptom-score-calculating',
      type: 'SymptomScoreCalculatingScreenE7',
      hideProgressBar: true,
      props: {},
    },
    {
      path: '/e10/symptom-score',
      type: 'SymptomScoreResultsScreenE7',
      hideProgressBar: true,
      props: {},
    },

    // ============================================================
    // SECTION 6a: Future you — no weight loss (symptom reduction graph)
    // ============================================================
    {
      path: '/e10/future-score',
      type: 'FutureScoreScreenE7',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      showWhen: { no_weight_loss_goal: 'true' },
      props: { projectionGraph: true },
    },

    // ============================================================
    // SECTION 6b: Future you — weight loss
    // ============================================================
    {
      path: '/e10/future-score-weight-loss',
      type: 'FutureScoreScreenE7',
      backgroundColor: 'var(--colour-surface-cream-subtle)',
      showWhen: { has_weight_loss_goal: 'true' },
      props: { weightLoss: true, projectionGraph: true },
    },

    // ============================================================
    // SECTION 7: Wrap-up (convergence — all users)
    // ============================================================
    {
      path: '/e10/email',
      type: 'EmailCaptureScreen',
      props: {
        title: 'Get your personalised plan and free gut health guide',
        topImage: '/img/e4/email-guide.png',
        topImageAspectRatio: '1005 / 471',
        buttonText: 'Continue',
        footerBody:
          "We'll also send you ongoing science and nutrition emails, news and offers.{br}{optOutLink}. See our {privacyPolicy}.",
        showMarketingConsent: true,
        defaultMarketingOptIn: true,
      },
    },
    {
      path: '/e10/results',
      type: 'ResultsLoadingScreen',
      hideProgressBar: true,
      props: {
        title: 'We are building your personalised plan',
        steps: [
          'Analysing your health data',
          'Reviewing your goals',
          'Personalising your insights',
          'Generating your action plan',
        ],
        trustedByText: 'Trusted by over 3 million',
        testimonials: [
          {
            name: 'Alison',
            quote:
              'I\u2019ve noticed a change in my digestion, and I\u2019ve noticed a change in my weight. And that\u2019s all without restricting anything.',
          },
          {
            name: 'Selena',
            quote:
              'I feel more confident about food, food choices and regaining my independence, and I am seeing the difference in my energy.',
          },
          {
            name: 'Patty',
            quote:
              'Admittedly, I have been a sugar addict. I have noticed this week that I am no longer desiring highly processed, high sugar candy!',
          },
          {
            name: 'Kate',
            quote: 'Changed my mindset to focus on whole foods and not calorie content.',
          },
          {
            name: 'Dorna',
            quote:
              'Before Zoe, I think I was feeling lethargic. And now I feel a lot healthier and I\u2019m more energetic. And I have the energy to exercise more go out on longer hikes.',
          },
        ],
        buttonText: 'Discover my plan',
        exitPath: GB_EXIT_PATH,
      },
    },
  ],
}

export default e10
